import React, { useEffect, useState, useCallback } from 'react';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import '../css/Allocate_Facilitators.css';

// --- ICONS and UI Components (No changes needed here) ---
const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
);
const EmptyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75l-2.489-2.489m0 0a3.375 3.375 0 10-4.773-4.773 3.375 3.375 0 004.774 4.774zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);
const LoadingSpinner = ({ message }) => (
    <div className="loading-overlay">
        <div className="spinner"></div>
        <p>{message}</p>
    </div>
);
const EmptyState = ({ message, itemType }) => (
    <div className="empty-state">
        <EmptyIcon />
        <h3>{message}</h3>
        <p>There are currently no {itemType} to display.</p>
    </div>
);

// ✅ FIX 1: Define API_ENDPOINTS outside the component.
// This makes it a stable constant that won't cause re-renders.
const API_ENDPOINTS = {
    getStudents: 'https://ethiccraft.org/backend/profile/read.php',
    getStudentDetails: 'https://ethiccraft.org/backend/profile/read.php',
    getApprovedFacilitators: 'https://ethiccraft.org/backend/Facilitator_Panel/read.php',
    allocateMentor: 'https://ethiccraft.org/backend/Facilitator_Panel/allocate_mentor.php',
    checkAreaLeaderStatus: 'https://ethiccraft.org/backend/Facilitator_Panel/get_area_leaders.php'
};


const StudentAllocation = () => {
    // --- State Management ---
    const [students, setStudents] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [facilitators, setFacilitators] = useState([]);
    const [allocations, setAllocations] = useState({});
    const [searchText, setSearchText] = useState('');

    // --- Loading & Authorization States ---
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isSaving, setIsSaving] = useState({});

    // --- Data Fetching and Authorization ---
    const checkAuthorizationAndFetchData = useCallback(async () => {
        setIsLoading(true);
        const userRole = Cookies.get('role');
        const userEmail = Cookies.get('email');

        let authorized = false;

        if (userRole === 'admin') {
            authorized = true;
        } else if (userRole === 'area_leader') {
            if (!userEmail) {
                Swal.fire({ icon: 'error', title: 'Session Error', text: 'Your email could not be found. Please log in again.' }).then(() => window.history.back());
                setIsLoading(false);
                return;
            }
            try {
                const res = await fetch(API_ENDPOINTS.checkAreaLeaderStatus);
                const leaders = await res.json();
                const currentUser = Array.isArray(leaders) ? leaders.find(l => (l.Email || l.email)  === userEmail && l.Approved === '1') : null;
                if (currentUser) {
                    authorized = true;
                } else {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Account Not Approved',
                        text: 'Your account is pending approval from an administrator. You cannot allocate students yet.',
                        confirmButtonText: 'Go Back'
                    }).then(() => window.history.back());
                }
            } catch (error) {
                Swal.fire({ icon: 'error', title: 'Authorization Failed', text: 'Could not verify your account status.' }).then(() => window.history.back());
            }
        } else {
            Swal.fire({ icon: 'error', title: 'Unauthorized Access', text: 'You do not have permission to view this page.' }).then(() => window.history.back());
        }

        if (!authorized) {
            setIsLoading(false);
            return;
        }

        setIsAuthorized(true);

        try {
            const [studentRes, facilitatorRes] = await Promise.all([
                fetch(API_ENDPOINTS.getStudents),
                fetch(API_ENDPOINTS.getApprovedFacilitators)
            ]);

            if (!studentRes.ok || !facilitatorRes.ok) throw new Error('Failed to fetch initial data.');

            const studentData = await studentRes.json();
            const facilitatorData = await facilitatorRes.json();
            
            const normalizedStudents = Array.isArray(studentData) ? studentData.map(s => ({
                id: s.user_id,
                name: s.full_name,
                email: s.email,
                allocatedMentor: s.Mentors_Name || 'N/A',
                phone_number: s.phone_number || 'N/A',
                department: s.department || 'N/A',
                College: s.college || 'N/A'
            })) : [];

            const unallocated = normalizedStudents.filter(s => s.allocatedMentor === 'N/A').sort((a,b) => a.name.localeCompare(b.name));
            const allocated = normalizedStudents.filter(s => s.allocatedMentor !== 'N/A').sort((a,b) => a.name.localeCompare(b.name));
            const sortedStudents = [...unallocated, ...allocated];

            setStudents(sortedStudents);
            setFilteredStudents(sortedStudents);
            
            const initialAllocs = {};
            normalizedStudents.forEach(s => { initialAllocs[s.id] = s.allocatedMentor; });
            setAllocations(initialAllocs);

            setFacilitators(Array.isArray(facilitatorData) ? facilitatorData : []);

        } catch (error) {
            console.error("Data Fetch Error:", error);
            Swal.fire({ icon: 'error', title: 'Data Load Failed', text: 'Could not load student or facilitator data.' });
        } finally {
            setIsLoading(false);
        }
    // ✅ FIX 2: The dependency array for useCallback is now stable because API_ENDPOINTS is stable.
    // However, since it's a module-level constant, it's not strictly needed here,
    // but we'll leave the function dependency in the useEffect hook.
    }, []); 


    useEffect(() => {
        checkAuthorizationAndFetchData();
    // ✅ FIX 3: This dependency is now stable, so this effect will only run once on mount.
    }, [checkAuthorizationAndFetchData]);
    

    useEffect(() => {
        const lowerCaseSearch = searchText.toLowerCase();
        const filtered = students.filter(student =>
            student.name.toLowerCase().includes(lowerCaseSearch) ||
            student.email.toLowerCase().includes(lowerCaseSearch)
        );
        setFilteredStudents(filtered);
    }, [searchText, students]);

    const handleAllocationChange = (studentId, facilitatorEmail) => {
        setAllocations(prev => ({ ...prev, [studentId]: facilitatorEmail }));
    };

    const handleViewDetails = async (student) => {
        // ... (This handler is fine, no changes needed)
        Swal.fire({
            title: `<strong>${student.name}</strong>`,
            html: `<p>Fetching details...</p>`,
            showCloseButton: true,
            showConfirmButton: false,
            didOpen: async () => {
                const detailsHTML = `
                    <div class="student-details-modal">
                        <p><strong>Email:</strong> ${student.email}</p>
                        <p><strong>Current Mentor:</strong> ${allocations[student.id] === 'N/A' ? 'Not Assigned' : allocations[student.id]}</p>
                        <p><strong>Phone:</strong> ${student.phone_number || 'N/A'}</p>
                        <p><strong>Department:</strong> ${student.department || 'N/A'}</p>
                        <p><strong>University:</strong> ${student.College || 'N/A'}</p
                    </div>
                `;
                Swal.update({ html: detailsHTML });
            }
        });
    };
    
    const handleSaveAllocation = async (studentId) => {
        setIsSaving(prev => ({ ...prev, [studentId]: true }));
        const facilitator_Name = allocations[studentId];

        try {
            const res = await fetch(API_ENDPOINTS.allocateMentor, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    student_id: studentId,
                    mentor_name: facilitator_Name
                })
            });
            const result = await res.json();

            if (result.success) {
                Swal.fire({
                    toast: true, icon: 'success', title: 'Allocation Saved!',
                    position: 'top-end', showConfirmButton: false, timer: 3000, timerProgressBar: true,
                });

                // ✅ IMPROVEMENT: Update both student lists to keep UI in sync
                const updateState = (prev) => prev.map(s => 
                    s.id === studentId ? { ...s, allocatedMentor: facilitator_Name } : s
                );
                setStudents(updateState);
                setFilteredStudents(updateState);

            } else {
                throw new Error(result.message || 'Failed to save allocation.');
            }
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save Failed', text: error.message });
        } finally {
            setIsSaving(prev => ({ ...prev, [studentId]: false }));
        }
    };


    // --- Render Logic (No changes needed here) ---
    if (isLoading) {
        return <LoadingSpinner message="Verifying access and loading data..." />;
    }

    if (!isAuthorized) {
        return null; 
    }

    return (
        <div className="main">
            <div className="student-allocation-container">
                <div className="container-header">
                    <h1 className="header-title">Student Allocation</h1>
                    <div className="allocate-search-box">
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder="Search students by name or email..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </div>
                </div>

                <div className="table-wrapper">
                    {filteredStudents.length > 0 ? (
                        <table className="allocation-table">
                            <thead>
                                <tr>
                                    <th>Student Name</th>
                                    <th>Details</th>
                                    <th>Allocate Mentor</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStudents.map((student) => (
                                    <tr key={student.id} className={allocations[student.id] === 'N/A' ? 'unallocated-row' : ''}>
                                        <td>
                                            <div className="student-info">
                                                <span className="student-name">{student.name}</span>
                                                <span className="student-email">{student.email}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <button className="btn btn-view" onClick={() => handleViewDetails(student)}>
                                                View
                                            </button>
                                        </td>
                                        <td>
                                            <select
                                                className="mentor-select"
                                                value={allocations[student.id] || 'N/A'}
                                                onChange={(e) => handleAllocationChange(student.id, e.target.value)}
                                            >
                                                <option value="N/A">N/A</option>
                                                {facilitators.map(fac => (
                                                    <option key={fac.Email} value={fac.Full_Name}>
                                                        {fac.Full_Name}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-save"
                                                onClick={() => handleSaveAllocation(student.id)}
                                                disabled={isSaving[student.id] || allocations[student.id] === student.allocatedMentor}
                                            >
                                                {isSaving[student.id] ? 'Saving...' : 'Save'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <EmptyState message="No Students Found" itemType="students" />
                    )}
                </div>
            </div>
        </div>
    );
};

export default StudentAllocation;